import React, { useState } from 'react';
import { Box, Button, TextField, Typography, Paper, Alert, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { apiRequest } from '../../../utils/fetch';
import { EmailEventInterface, GenericResponseInterface } from '../../../interfaces/interfaces';

const initialEvent: Partial<EmailEventInterface> = {
  eventName: '',
  subject: '',
  templateName: '',
  description: '',
};

const EmailEventsAdminAdd: React.FC = () => {
  const [event, setEvent] = useState<Partial<EmailEventInterface>>(initialEvent);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleChange = (field: keyof EmailEventInterface, value: any) => {
    setEvent(e => ({ ...e, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      const result = await apiRequest<GenericResponseInterface<EmailEventInterface>>('/emailEvents/insert', 'POST', event);
      if (result.status) {
        setSuccess(result.message || 'Email event created successfully! Cache auto-refreshed.');
        setDialogOpen(true);
        setEvent(initialEvent);
      } else {
        setError(result.message || 'Failed to create email event');
        setDialogOpen(true);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create email event');
      setDialogOpen(true);
    }
  };

  return (
    <Paper sx={{ p: 3, maxWidth: 600, mx: 'auto', mt: 4 }}>
      <Typography variant="h5" gutterBottom>Add New Email Event</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      <form onSubmit={handleSubmit}>
        <TextField
          label="Event Name"
          value={event.eventName ?? ''}
          onChange={e => handleChange('eventName', e.target.value)}
          fullWidth
          margin="dense"
          required
          helperText="Unique identifier for this email event (e.g., 'account_creation_success')"
        />
        <TextField
          label="Subject"
          value={event.subject ?? ''}
          onChange={e => handleChange('subject', e.target.value)}
          fullWidth
          margin="dense"
          required
          helperText="Email subject line"
        />
        <TextField
          label="Template Name"
          value={event.templateName ?? ''}
          onChange={e => handleChange('templateName', e.target.value)}
          fullWidth
          margin="dense"
          required
          helperText="Handlebars template filename (e.g., 'accountCreation')"
        />
        <TextField
          label="Description"
          value={event.description ?? ''}
          onChange={e => handleChange('description', e.target.value)}
          fullWidth
          margin="dense"
          multiline
          rows={3}
          helperText="Brief description of when this email is sent"
        />
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button type="submit" variant="contained" color="primary">
            Create Email Event
          </Button>
        </Box>
      </form>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{success ? 'Success' : 'Error'}</DialogTitle>
        <DialogContent>
          <Typography>{success || error}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default EmailEventsAdminAdd;
